import type { PipelineDebugStreamEvent } from '@evimo/types/api/pipeline-debug/pipeline-debug-stream.types';

export type PipelineStreamCallbacks = {
  onStart?: (total: number) => void;
  onProgress?: (progress: {
    index: number;
    total: number;
    logId?: string;
  }) => void;
  onItem?: (item: {
    index: number;
    total: number;
    logId?: string;
    partIndex?: number;
    partTotal?: number;
    output: unknown;
    error?: string;
  }) => void;
  onComplete?: (result: {
    output: unknown;
    successCount: number;
    errorCount: number;
    total: number;
  }) => void;
  onError?: (message: string) => void;
};

function dispatchStreamEvent(
  event: PipelineDebugStreamEvent,
  callbacks: PipelineStreamCallbacks,
): unknown | undefined {
  switch (event.type) {
    case 'start':
      callbacks.onStart?.(event.total);
      return undefined;
    case 'progress':
      callbacks.onProgress?.({
        index: event.index,
        total: event.total,
        logId: event.logId,
      });
      return undefined;
    case 'item':
      callbacks.onItem?.({
        index: event.index,
        total: event.total,
        logId: event.logId,
        partIndex: event.partIndex,
        partTotal: event.partTotal,
        output: event.output,
        error: event.error,
      });
      return undefined;
    case 'complete':
      callbacks.onComplete?.({
        output: event.output,
        successCount: event.successCount,
        errorCount: event.errorCount,
        total: event.total,
      });
      return event.output;
    case 'error':
      callbacks.onError?.(event.message);
      throw new Error(event.message);
    default:
      return undefined;
  }
}

function parseNdjsonLine(
  line: string,
  callbacks: PipelineStreamCallbacks,
): PipelineDebugStreamEvent | null {
  try {
    return JSON.parse(line) as PipelineDebugStreamEvent;
  } catch {
    callbacks.onError?.(`Malformed NDJSON line: ${line}`);
    return null;
  }
}

function consumeNdjsonLine(
  line: string,
  callbacks: PipelineStreamCallbacks,
): unknown | undefined {
  const event = parseNdjsonLine(line, callbacks);
  if (!event) {
    return undefined;
  }

  return dispatchStreamEvent(event, callbacks);
}

export async function readNdjsonStream(
  body: ReadableStream<Uint8Array>,
  callbacks: PipelineStreamCallbacks,
): Promise<unknown> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalOutput: unknown;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        continue;
      }

      const output = consumeNdjsonLine(trimmed, callbacks);
      if (output !== undefined) {
        finalOutput = output;
      }
    }
  }

  const trailing = buffer.trim();
  if (trailing) {
    const output = consumeNdjsonLine(trailing, callbacks);
    if (output !== undefined) {
      finalOutput = output;
    }
  }

  return finalOutput;
}
